
import React, { useState } from 'react';
import './WarrantyClaim.css';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || '';

const WarrantyClaim = () => {
  const [formData, setFormData] = useState({
    // Distribution Partner Information
    claimType: '', // 'warranty-inspection', 'service-repair', 'firstup-failure'
    proofOfPurchase: null,
    invoiceNumber: '', // New field for invoice number
    proofMethod: '', // 'upload' or 'invoice' - tracks which method is selected
    // End User Information
    fullName: '',
    phoneNumber: '',
    email: '',
    address: '',
    
    // Products Array 
    products: [
      {
        modelNumber: '',
        serialNumber: '',
        faultDescription: ''
      }
    ],
    
    // Common fault description for all products (optional)
    commonFaultDescription: '',
    
    // Fault images (common for all products)
    faultImages: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [claimId, setClaimId] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [apiError, setApiError] = useState('');
  
  // Handle text/select inputs for main form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
    
    // Clear API error when user types
    if (apiError) setApiError('');
  };
  
  // Handle product field changes
  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value
    };
    
    setFormData({
      ...formData,
      products: updatedProducts
    });
    
    // Clear product-specific errors
    const errorKey = `products[${index}].${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };
  
  // Add a new product
  const addProduct = () => {
    if (formData.products.length >= 5) {
      setErrors({
        ...errors,
        products: 'Maximum 5 products per claim'
      });
      return;
    }
    
    setFormData({
      ...formData,
      products: [
        ...formData.products,
        {
          modelNumber: '',
          serialNumber: '',
          faultDescription: ''
        }
      ]
    });
    
    // Clear products error if exists
    if (errors.products) {
      setErrors({
        ...errors,
        products: ''
      });
    }
  };
  
  // Remove a product
  const removeProduct = (index) => {
    if (formData.products.length <= 1) {
      setErrors({
        ...errors,
        products: 'At least one product is required'
      });
      return;
    }
    
    setFormData({
      ...formData,
      products: formData.products.filter((_, i) => i !== index)
    });
  };
  
  // Handle file uploads
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    
    if (name === 'proofOfPurchase') {
      if (files.length > 0) {
        // Validate file size (5MB max)
        const file = files[0];
        if (file.size > 5 * 1024 * 1024) {
          setErrors({
            ...errors,
            proofOfPurchase: 'File size must be less than 5MB'
          });
          return;
        }
        
        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
          setErrors({
            ...errors,
            proofOfPurchase: 'File must be JPG, PNG, or PDF'
          });
          return;
        }
        
        setFormData({
          ...formData,
          proofOfPurchase: file,
          proofMethod: 'upload' // method to upload when file is selected

        });
        
        // Clear any existing error
        if (errors.proofOfPurchase) {
          setErrors({
            ...errors,
            proofOfPurchase: ''
          });
        }
        if (errors.invoiceNumber) {
          setErrors({
            ...errors,
            invoiceNumber: ''
          });
        }
        if (errors.proofMethod) {
          setErrors({
            ...errors,
            proofMethod: ''
          });
        }

      }
    } else if (name === 'faultImages') {
      // Handle multiple images
      const imageFiles = Array.from(files);
      
      // Validate total images (max 5)
      const totalImages = formData.faultImages.length + imageFiles.length;
      if (totalImages > 5) {
        setErrors({
          ...errors,
          faultImages: 'Maximum 5 images allowed'
        });
        return;
      }
      
      // Validate each image
      const validImages = imageFiles.filter(file => {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        return validTypes.includes(file.type) && file.size <= 5 * 1024 * 1024;
      });
      
      setFormData({
        ...formData,
        [name]: [...formData.faultImages, ...validImages].slice(0, 5)
      });
      
      // Clear any existing error
      if (errors.faultImages) {
        setErrors({
          ...errors,
          faultImages: ''
        });
      }
    }
  };
  
  // Handle checkbox/radio changes
  const handleClaimTypeChange = (type) => {
    setFormData({
      ...formData,
      claimType: type,
      proofOfPurchase: null,
      invoiceNumber: '',
      proofMethod: ''

    });
    
    // Clear related errors
    if (errors.claimType) {
      setErrors({
        ...errors,
        claimType: ''
      });
    }
        if (errors.proofMethod) {
      setErrors({
        ...errors,
        proofMethod: ''
      });
    }
  };
  
  // Handle proof method selection (upload or invoice)
  const handleProofMethodChange = (method) => {
    setFormData({
      ...formData,
      proofMethod: method,
      // Clear the other method's data
      ...(method === 'upload' && { invoiceNumber: '' }),
      ...(method === 'invoice' && { proofOfPurchase: null })
    });
    
    // Clear errors
    if (errors.proofMethod) {
      setErrors({
        ...errors,
        proofMethod: ''
      });
    }
    if (errors.proofOfPurchase) {
      setErrors({
        ...errors,
        proofOfPurchase: ''
      });
    }
    if (errors.invoiceNumber) {
      setErrors({
        ...errors,
        invoiceNumber: ''
      });
    }

  };
  
  // Remove an uploaded image
  const removeImage = (index) => {
    const updatedImages = formData.faultImages.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      faultImages: updatedImages
    });
  };
  
  // Remove proof of purchase
  const removeProofOfPurchase = () => {
    setFormData({
      ...formData,
      proofOfPurchase: null,
       proofMethod: formData.invoiceNumber ? 'invoice' : ''
    });
  };
   const clearInvoiceNumber = () => {

    setFormData({
      ...formData,
      invoiceNumber: '',
      proofMethod: formData.proofOfPurchase ? 'upload' : ''
   });
   };
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Claim Type Validation
    if (!formData.claimType) {
      newErrors.claimType = 'Please select a claim type';
    }
    
    // Proof of Purchase Validation (not required for service-repair)
     if (formData.claimType !== 'service-repair') {
      // Check if either method is selected
      if (!formData.proofMethod) {
        newErrors.proofMethod = 'Please provide proof of purchase';
      } else if (formData.proofMethod === 'upload' && !formData.proofOfPurchase) {
        newErrors.proofOfPurchase = 'Please upload proof of purchase';
      } else if (formData.proofMethod === 'invoice' && !formData.invoiceNumber.trim()) {
        newErrors.invoiceNumber = 'Please enter invoice number';
      } else if (formData.proofMethod === 'invoice' && formData.invoiceNumber.trim().length < 3) {
        newErrors.invoiceNumber = 'Invoice number must be at least 3 characters';
      }
    }
    
    // End User Validation
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    
    // Product Validation
    formData.products.forEach((product, index) => {
      if (!product.modelNumber.trim()) {
        newErrors[`products[${index}].modelNumber`] = 'Model number is required';
      }
      
      if (!product.faultDescription.trim()) {
        newErrors[`products[${index}].faultDescription`] = 'Description of fault is required';
      } else if (product.faultDescription.length > 500) {
        newErrors[`products[${index}].faultDescription`] = 'Description cannot exceed 500 characters';
      }
    });
    
    // Validate common fault description if provided
    if (formData.commonFaultDescription && formData.commonFaultDescription.length > 1000) {
      newErrors.commonFaultDescription = 'Common description cannot exceed 1000 characters';
    }
    
    // File validation
    if (formData.proofOfPurchase && formData.proofOfPurchase.size > 5 * 1024 * 1024) {
      newErrors.proofOfPurchase = 'Proof of purchase must be less than 5MB';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Upload proof of purchase
  const uploadProofOfPurchase = async (claimId, file) => {
    if (!file) return { success: true, message: 'No file to upload' };
    
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await fetch(`${API_BASE_URL}/warrantyclaims/${claimId}/upload-proof`, {
        method: 'POST',
        body: uploadFormData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return { 
          success: false, 
          message: errorText || 'Failed to upload proof of purchase' 
        };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error uploading proof of purchase:', error);
      return { 
        success: false, 
        message: 'Network error uploading proof of purchase' 
      };
    }
  };
  
  // Upload fault images
  const uploadFaultImages = async (claimId, images) => {
    if (!images || images.length === 0) return { success: true, message: 'No images to upload' };
    
    const uploadFormData = new FormData();
    images.forEach(image => {
      uploadFormData.append('files', image);
    });
    
    try {
      const response = await fetch(`${API_BASE_URL}/warrantyclaims/${claimId}/upload-images`, {
        method: 'POST',
        body: uploadFormData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return { 
          success: false, 
          message: errorText || 'Failed to upload images' 
        };
      }
      
      return await response.json();
    } catch (error) {
      return { 
        success: false, 
        message: 'Network error uploading images' 
      };
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setClaimId('');
    setClaimNumber('');
    setApiError('');
    
    try {
      // Step 1: Create the warranty claim with multiple products
      const claimData = {
        claimType: formData.claimType,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        address: formData.address,
        commonFaultDescription: formData.commonFaultDescription,
        proofMethod: formData.proofMethod,
        invoiceNumber: formData.invoiceNumber || null,
        products: formData.products.map(product => ({
          modelNumber: product.modelNumber,
          serialNumber: product.serialNumber,
          faultDescription: product.faultDescription
        }))
      };
      
      console.log('Submitting claim data:', claimData);
      
      const createResponse = await fetch(`${API_BASE_URL}/warrantyclaims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(claimData)
      });
      
      if (!createResponse.ok) {
        let errorMessage = 'Failed to create warranty claim';
        try {
          const errorData = await createResponse.json();
          errorMessage = errorData.message || errorMessage;
        } catch (jsonError) {
          const errorText = await createResponse.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const createdClaim = await createResponse.json();
      
      const newClaimId = createdClaim.id;
      const newClaimNumber = createdClaim.claimNumber;
      
      setClaimId(newClaimId);
      setClaimNumber(newClaimNumber);
      
      // Step 2: Upload proof of purchase (if required and provided)
      if (formData.claimType !== 'service-repair' &&  formData.proofMethod === 'upload' && formData.proofOfPurchase) {
        const proofResult = await uploadProofOfPurchase(newClaimId, formData.proofOfPurchase);
        if (!proofResult.success) {
          console.warn('Proof of purchase upload failed:', proofResult.message);
          // Continue anyway - don't fail the whole claim
        }
      }
      
      // Step 3: Upload fault images (if provided)
      if (formData.faultImages.length > 0) {
        const imagesResult = await uploadFaultImages(newClaimId, formData.faultImages);
        if (!imagesResult.success) {
          console.warn('Fault images upload failed:', imagesResult.message);
          // Continue anyway - don't fail the whole claim
        }
      }
      
      // Success message
      alert(`✅ Warranty claim submitted successfully!\n\nClaim Number: ${newClaimNumber}\nProducts: ${formData.products.length}\n\nPlease save your claim number for future reference.`);
      
      // Reset form
      setFormData({
        claimType: '',
        proofOfPurchase: null,
        invoiceNumber: '',
        proofMethod: '',
        fullName: '',
        phoneNumber: '',
        email: '',
        address: '',
        products: [
          {
            modelNumber: '',
            serialNumber: '',
            faultDescription: ''
          }
        ],
        commonFaultDescription: '',
        faultImages: []
      });
      
      // Clear all errors
      setErrors({});
      
    } catch (error) {
      console.error('Submission error:', error);
      setApiError(error.message);
      alert(`❌ Error submitting claim: ${error.message}\n\nPlease try again or contact support.`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Character counter for common fault description
  const commonFaultDescriptionLength = formData.commonFaultDescription.length;
  
  // Render product sections
  const renderProductSections = () => {
    return formData.products.map((product, index) => {
      const productError = errors.products;
      const modelError = errors[`products[${index}].modelNumber`];
      const faultError = errors[`products[${index}].faultDescription`];
      
      return (
        <div key={index} className="product-section">
          <div className="product-section-header">
            <h3 className="product-title">
              Product {index + 1}
              {index === 0 && <span className="required-badge">*</span>}
            </h3>
            {formData.products.length > 1 && (
              <button 
                type="button"
                className="remove-product-btn"
                onClick={() => removeProduct(index)}
              >
                Remove
              </button>
            )}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor={`modelNumber-${index}`}>
                Model Number *
              </label>
              <input
                type="text"
                id={`modelNumber-${index}`}
                value={product.modelNumber}
                onChange={(e) => handleProductChange(index, 'modelNumber', e.target.value)}
                placeholder="Enter product model number"
                className={modelError ? 'error' : ''}
              />
              {modelError && <span className="field-error">{modelError}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor={`serialNumber-${index}`}>
                Serial Number
                <span className="field-note">(Optional)</span>
              </label>
              <input
                type="text"
                id={`serialNumber-${index}`}
                value={product.serialNumber}
                onChange={(e) => handleProductChange(index, 'serialNumber', e.target.value)}
                placeholder="Enter product serial number"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor={`faultDescription-${index}`}>
              Description of Fault *
              <span className="char-counter">
                {product.faultDescription.length}/500 characters
              </span>
            </label>
            <textarea
              id={`faultDescription-${index}`}
              value={product.faultDescription}
              onChange={(e) => handleProductChange(index, 'faultDescription', e.target.value)}
              placeholder="Describe the specific issue with this product..."
              rows="3"
              maxLength="500"
              className={faultError ? 'error' : ''}
            />
            {faultError && <span className="field-error">{faultError}</span>}
          </div>
          
          {index < formData.products.length - 1 && <hr className="product-divider" />}
        </div>
      );
    });
  };

  return (
    <div className="warranty-claim-container">
      <div className="warranty-claim-wrapper">
        <div className="warranty-claim-header">
          <h1>Submit Warranty Claim</h1>
          <p>You can submit claims for up to 5 products at once</p>
        </div>
        
        {apiError && (
          <div className="error-banner">
            <p><strong>Error:</strong> {apiError}</p>
          </div>
        )}
        
        {claimNumber && (
          <div className="success-banner">
            <p>✅ Claim submitted successfully!</p>
            <p>Your claim number is: <strong>{claimNumber}</strong></p>
            <p>Please save your claim number for future reference.</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="warranty-claim-form">
          
          {/* SECTION 1: DISTRIBUTION PARTNER INFORMATION */}
          <div className="form-section">
            <div className="section-header">
              <h2>Claim Selection</h2>
              <div className="section-divider"></div>
            </div>
            
            <div className="claim-type-selection">
              <p className="section-subtitle"></p>
              <div className="claim-type-options">
                <div 
                  className={`claim-type-option ${formData.claimType === 'warranty-inspection' ? 'selected' : ''}`}
                  onClick={() => handleClaimTypeChange('warranty-inspection')}
                >
                  <div className="option-header">
                    <span className="option-checkbox">
                      {formData.claimType === 'warranty-inspection' && '✓'}
                    </span>
                    <span className="option-title">Warranty Inspection</span>
                  </div>
                  <p className="option-description">For products under warranty period requiring inspection (proof of purchase required)</p>
                </div>
                
                <div 
                  className={`claim-type-option ${formData.claimType === 'service-repair' ? 'selected' : ''}`}
                  onClick={() => handleClaimTypeChange('service-repair')}
                >
                  <div className="option-header">
                    <span className="option-checkbox">
                      {formData.claimType === 'service-repair' && '✓'}
                    </span>
                    <span className="option-title">Service Repair Inspection</span>
                  </div>
                  <p className="option-description">Proof of purchase not required for service repairs (Minimum Charge 50$)</p>
                </div>
                
                <div 
                  className={`claim-type-option ${formData.claimType === 'firstup-failure' ? 'selected' : ''}`}
                  onClick={() => handleClaimTypeChange('firstup-failure')}
                >
                  <div className="option-header">
                    <span className="option-checkbox">
                      {formData.claimType === 'firstup-failure' && '✓'}
                    </span>
                    <span className="option-title">Firstup Failure</span>
                  </div>
                  <p className="option-description">For products that failed immediately after purchase</p>
                </div>
              </div>
              {errors.claimType && <span className="field-error">{errors.claimType}</span>}
            </div>
            
            {/* Proof of Purchase Upload OR Invoice Number */}
            {formData.claimType !== 'service-repair' && (
              <div className="proof-of-purchase-section">
                <div className="form-group">
                  <label>
                    Proof of Purchase *
                    <span className="file-requirements">(Required for warranty claims)</span>
                  </label>
                  
                  {/* Proof Method Selection */}
                  <div className="proof-method-options">
                    <div className="proof-method-row">
                      <div 
                        className={`proof-method-option ${formData.proofMethod === 'upload' ? 'selected' : ''}`}
                        onClick={() => handleProofMethodChange('upload')}
                      >
                        <span className="method-checkbox">
                          {formData.proofMethod === 'upload' && '✓'}
                        </span>
                        <span className="method-title">Upload Document</span>
                        <span className="method-hint">(Invoice, receipt, or purchase confirmation)</span>
                      </div>
                      
                      <div 
                        className={`proof-method-option ${formData.proofMethod === 'invoice' ? 'selected' : ''}`}
                        onClick={() => handleProofMethodChange('invoice')}
                      >
                        <span className="method-checkbox">
                          {formData.proofMethod === 'invoice' && '✓'}
                        </span>
                        <span className="method-title">Enter Invoice Number</span>
                        <span className="method-hint">(If you have the invoice number)</span>
                      </div>
                    </div>
                    {errors.proofMethod && <span className="field-error">{errors.proofMethod}</span>}
                  </div>
                  
                  {/* Upload Document Option */}
                  {formData.proofMethod === 'upload' && (
                    <div className="file-upload-section">
                      {!formData.proofOfPurchase ? (
                        <div className="file-upload-area">
                          <input
                            type="file"
                            id="proofOfPurchase"
                            name="proofOfPurchase"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png,.pdf"
                            className="file-input"
                          />
                          <div className="upload-placeholder">
                            <span className="upload-icon">📎</span>
                            <p>Click to upload proof of purchase</p>
                            <p className="upload-hint">Accepted: JPG, PNG, PDF up to 5MB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="uploaded-file">
                          <span className="file-icon">📄</span>
                          <span className="file-name">{formData.proofOfPurchase.name}</span>
                          <span className="file-size">
                            {(formData.proofOfPurchase.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                          <button 
                            type="button" 
                            className="remove-file"
                            onClick={removeProofOfPurchase}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                      {errors.proofOfPurchase && <span className="field-error">{errors.proofOfPurchase}</span>}
                    </div>
                  )}
                  
                  {/* Invoice Number Option */}
                  {formData.proofMethod === 'invoice' && (
                    <div className="invoice-input-section">
                      <div className="form-group">
                        <label htmlFor="invoiceNumber">
                          Invoice Number *
                          <span className="field-note">(Enter your purchase invoice number)</span>
                        </label>
                        <div className="invoice-input-wrapper">
                          <input
                            type="text"
                            id="invoiceNumber"
                            name="invoiceNumber"
                            value={formData.invoiceNumber}
                            onChange={handleChange}
                            placeholder="Enter invoice number (e.g., INV-12345)"
                            className={errors.invoiceNumber ? 'error' : ''}
                          />
                          {formData.invoiceNumber && (
                            <button 
                              type="button"
                              className="clear-invoice-btn"
                              onClick={clearInvoiceNumber}
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {errors.invoiceNumber && <span className="field-error">{errors.invoiceNumber}</span>}
                        <p className="field-hint">
                          Please enter the exact invoice number from your purchase receipt
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* SECTION 2: END USER INFORMATION */}
          <div className="form-section">
            <div className="section-header">
              <h2>CUSTOMER INFORMATION</h2>
              <div className="section-divider"></div>
            </div>
            
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.fullName ? 'error' : ''}
              />
              {errors.fullName && <span className="field-error">{errors.fullName}</span>}
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className={errors.phoneNumber ? 'error' : ''}
                />
                {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="address">
                Address 
                  <span className="field-note">(Required for home delivery)</span>
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter your complete address"
                className={errors.address ? 'error' : ''}
              />
              {errors.address && <span className="field-error">{errors.address}</span>}
            </div>
          </div>
          
          {/* SECTION 3: PRODUCT INFORMATION */}
          <div className="form-section">
            <div className="section-header">
              <div className="section-header-top">
                <h2>PRODUCT INFORMATION</h2>
                <div className="products-controls">
                  <button 
                    type="button"
                    className="add-product-btn"
                    onClick={addProduct}
                    disabled={formData.products.length >= 5}
                  >
                    + Add Product
                  </button>
                  <span className="products-counter">
                    {formData.products.length}/5 products
                  </span>
                </div>
              </div>
              <div className="section-divider"></div>
              {errors.products && <span className="field-error">{errors.products}</span>}
            </div>
            
            {renderProductSections()}
            
            {/* Common Fault Description for multiple products */}
            {formData.products.length > 1 && (
              <div className="form-group common-fault-section">
                <label htmlFor="commonFaultDescription">
                  Common Fault Description (Optional)
                  <span className="field-note">
                    Describe issues common to all products
                  </span>
                  <span className="char-counter">
                    {commonFaultDescriptionLength}/1000 characters
                  </span>
                </label>
                <textarea
                  id="commonFaultDescription"
                  name="commonFaultDescription"
                  value={formData.commonFaultDescription}
                  onChange={handleChange}
                  placeholder="If all products have the same issue, describe it here..."
                  rows="3"
                  maxLength="1000"
                  className={errors.commonFaultDescription ? 'error' : ''}
                />
                {errors.commonFaultDescription && (
                  <span className="field-error">{errors.commonFaultDescription}</span>
                )}
                <p className="field-hint">
                  This description will be associated with all products in your claim
                </p>
              </div>
            )}
            
            {/* Fault Images Upload */}
            <div className="form-group">
              <label htmlFor="faultImages">
                Images of the Fault
                <span className="field-note">(Optional, max 5 images)</span>
              </label>
              
              <div className="image-upload-area">
                <input
                  type="file"
                  id="faultImages"
                  name="faultImages"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png"
                  multiple
                  className="file-input"
                  disabled={formData.faultImages.length >= 5}
                />
                
                <div className="upload-placeholder">
                  <span className="upload-icon">📷</span>
                  <p>Click to upload images of the fault</p>
                  <p className="upload-hint">
                    {formData.faultImages.length}/5 images uploaded
                    {formData.faultImages.length >= 5 && ' (Maximum reached)'}
                  </p>
                </div>
              </div>
              
              {/* Display uploaded images */}
              {formData.faultImages.length > 0 && (
                <div className="uploaded-images">
                  {formData.faultImages.map((image, index) => (
                    <div key={index} className="image-preview">
                      <img 
                        src={URL.createObjectURL(image)} 
                        alt={`Fault ${index + 1}`}
                        className="preview-image"
                      />
                      <button 
                        type="button"
                        className="remove-image"
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </button>
                      <span className="image-name">{image.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {errors.faultImages && <span className="field-error">{errors.faultImages}</span>}
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="form-submit">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting Claim for {formData.products.length} Product(s)...
                </>
              ) : (
                `Submit Claim for ${formData.products.length} Product(s)`
              )}
            </button>
            <p className="form-note">
              By submitting this form, you agree to our Warranty Terms and Conditions.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarrantyClaim;